import { Crown, Medal, Flame, TrendingUp, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LeaderboardEntry {
  id: string;
  wallet_address: string;
  total_volume: number | null;
  total_profit: number | null;
  win_rate: number | null;
  rank?: number | null;
}

interface TraderLeaderboardProps {
  entries: LeaderboardEntry[];
  isLoading: boolean;
}

export function TraderLeaderboard({ entries, isLoading }: TraderLeaderboardProps) {
  const formatUSD = (value: number | null) => {
    if (!value) return "$0";
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatAddress = (addr: string) => {
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500 animate-pulse" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-mono text-muted-foreground">#{rank}</span>;
  };

  const getRankStyles = (rank: number) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-500/20 to-yellow-500/5 border-yellow-500/40 shadow-lg shadow-yellow-500/10";
    if (rank === 2) return "bg-gradient-to-r from-gray-400/15 to-gray-400/5 border-gray-400/30";
    if (rank === 3) return "bg-gradient-to-r from-amber-600/15 to-amber-600/5 border-amber-600/30";
    return "bg-card border-border/50 hover:border-border";
  };

  const getTraderBadge = (profit: number | null, winRate: number | null) => {
    if ((profit ?? 0) > 10000) return { label: "Whale", color: "bg-primary/20 text-primary" };
    if ((winRate ?? 0) > 0.7) return { label: "Sharp", color: "bg-success/20 text-success" };
    if ((profit ?? 0) > 1000) return { label: "Pro", color: "bg-accent/20 text-accent" };
    return null;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!entries?.length) {
    return (
      <div className="p-12 text-center rounded-xl bg-card border border-border">
        <Crown className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
        <p className="text-lg font-medium text-foreground mb-2">No traders yet</p>
        <p className="text-muted-foreground">Start trading to appear on the leaderboard!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Top 3 Podium - Desktop */}
      <div className="hidden sm:grid grid-cols-3 gap-4 mb-6">
        {[1, 0, 2].map((podiumIndex) => {
          const entry = entries[podiumIndex];
          if (!entry) return <div key={podiumIndex} />;
          const rank = podiumIndex === 1 ? 2 : podiumIndex === 0 ? 1 : 3;
          const badge = getTraderBadge(entry.total_profit, entry.win_rate);

          return (
            <div
              key={entry.id}
              tabIndex={0}
              role="button"
              aria-label={`View trader ${formatAddress(entry.wallet_address)} details`}
              className={cn(
                "p-5 rounded-2xl border-2 text-center transition-all hover-lift focus-visible:ring-2 focus-visible:ring-primary",
                rank === 1 && "order-2 -mt-4",
                rank === 2 && "order-1",
                rank === 3 && "order-3",
                getRankStyles(rank)
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.open(`https://solscan.io/account/${entry.wallet_address}`, '_blank');
                }
              }}
              onClick={() => window.open(`https://solscan.io/account/${entry.wallet_address}`, '_blank')}
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-secondary flex items-center justify-center">
                {getRankIcon(rank)}
              </div>
              <p className="font-mono text-sm text-foreground mb-1 truncate">
                {formatAddress(entry.wallet_address)}
              </p>
              {badge && (
                <Badge className={cn("text-xs mb-2", badge.color)}>{badge.label}</Badge>
              )}
              <p className={cn(
                "text-xl font-mono font-bold",
                (entry.total_profit ?? 0) >= 0 ? "text-success" : "text-destructive"
              )}>
                {(entry.total_profit ?? 0) >= 0 ? "+" : ""}{formatUSD(entry.total_profit)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {((entry.win_rate ?? 0) * 100).toFixed(0)}% Win Rate
              </p>
            </div>
          );
        })}
      </div>

      {/* Full List */}
      <ScrollArea className="w-full rounded-xl border border-border">
        <div className="min-w-[600px]">
          <table className="w-full" role="table">
            <thead>
              <tr>
                <th scope="col" className="text-left p-3 text-xs sm:text-sm">Rank</th>
                <th scope="col" className="text-left p-3 text-xs sm:text-sm">Trader</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Volume</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Profit</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Win Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((entry, index) => {
                const rank = index + 1;
                const badge = getTraderBadge(entry.total_profit, entry.win_rate);

                return (
                  <tr key={entry.id} tabIndex={0} role="button" aria-label={`View trader ${formatAddress(entry.wallet_address)} details`} className={cn("hover:bg-secondary/50 transition-colors focus-visible:ring-2 focus-visible:ring-primary", getRankStyles(rank))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        window.open(`https://solscan.io/account/${entry.wallet_address}`, '_blank');
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = e.currentTarget.nextElementSibling as HTMLElement;
                        if (next) next.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = e.currentTarget.previousElementSibling as HTMLElement;
                        if (prev) prev.focus();
                      }
                    }}
                    onClick={() => window.open(`https://solscan.io/account/${entry.wallet_address}`, '_blank')}
                  >
                    <td className="p-3 text-xs sm:text-sm">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        {getRankIcon(rank)}
                      </div>
                    </td>
                    <td className="p-3 text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-sm text-foreground truncate">
                          {formatAddress(entry.wallet_address)}
                        </p>
                        {badge && (
                          <Badge className={cn("text-xs", badge.color)}>{badge.label}</Badge>
                        )}
                        {rank <= 3 && <Flame className="w-4 h-4 text-orange-500 animate-pulse" />}
                      </div>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className="text-sm font-mono text-foreground">
                        {formatUSD(entry.total_volume)}
                      </p>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className={cn(
                        "font-mono font-bold",
                        (entry.total_profit ?? 0) >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {(entry.total_profit ?? 0) >= 0 ? "+" : ""}{formatUSD(entry.total_profit)}
                      </p>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className="text-sm text-muted-foreground">
                        {((entry.win_rate ?? 0) * 100).toFixed(1)}%
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ScrollArea>
    </div>
  );
}
